import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Eye,
  Briefcase,
  ExternalLink,
  Star,
  Calendar,
  Users,
  TrendingUp,
  Award,
  CheckCircle,
  ArrowRight,
  Globe,
  Code,
} from "lucide-react";
import OptimizedImage from "@/components/ImageOptimization";

// Import portfolio images
import portfolioWeb1 from "@/assets/innovation-workspace.jpg";
import portfolioWeb2 from "@/assets/technology-workspace.jpg";
import portfolioMobile1 from "@/assets/mobile-development.jpg";
import portfolioMobile2 from "@/assets/innovation-lab.jpg";
import portfolioEnterprise1 from "@/assets/enterprise-solutions.jpg";
import portfolioEnterprise2 from "@/assets/sap-dashboard.jpg";
import portfolioAI1 from "@/assets/sap-s4hana-dashboard.jpg";
import portfolioAI2 from "@/assets/salesforce-einstein-crm.jpg";
import { useNavigate } from "react-router-dom";

interface PortfolioDialogProps {
  buttonText?: string;
  variant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  category?: string;
}

const PortfolioDialog = ({
  buttonText = "View Portfolio",
  variant = "outline",
  size = "default",
  className = "",
  category = "all",
}: PortfolioDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const navigate = useNavigate();

  const portfolioProjects = [
    // Healthcare & Medical
    {
      id: 1,
      title: "Hospital Management System",
      client: "MediCare Solutions",
      category: "healthcare",
      tags: ["Angular", "Java", "Oracle", "HIPAA"],
      image: portfolioEnterprise1,
      description:
        "Comprehensive healthcare management system with patient records, appointment scheduling, and billing integration.",
      challenge:
        "Fragmented systems causing inefficiencies and compliance issues",
      solution:
        "Unified platform with HIPAA-compliant data handling and integrated workflows",
      results: {
        efficiency: "50% reduction in administrative time",
        accuracy: "95% improvement in data accuracy",
        compliance: "100% HIPAA compliance",
        satisfaction: "92% patient satisfaction",
      },
      duration: "8 months",
      teamSize: "15 developers",
      technologies: [
        "Angular",
        "Spring Boot",
        "Oracle",
        "Docker",
        "Kubernetes",
      ],
      liveUrl: "https://medicare-portal.com",
      year: "2023",
    },
    {
      id: 2,
      title: "Telemedicine Platform",
      client: "HealthConnect Pro",
      category: "healthcare",
      tags: ["React", "WebRTC", "Node.js", "MongoDB"],
      image: portfolioWeb1,
      description:
        "Telemedicine platform enabling remote consultations with video conferencing and prescription management.",
      challenge:
        "Limited access to healthcare in remote areas and during pandemic",
      solution:
        "Secure telemedicine platform with HD video calls and integrated EHR",
      results: {
        patients: "10K+ remote consultations monthly",
        satisfaction: "4.9/5 patient satisfaction",
        costs: "40% reduction in healthcare costs",
        access: "300% increase in rural healthcare access",
      },
      duration: "6 months",
      teamSize: "12 developers",
      technologies: ["React", "WebRTC", "Node.js", "MongoDB", "Socket.io"],
      liveUrl: "https://telehealth.example.com",
      year: "2024",
    },

    // Financial Services & FinTech
    {
      id: 3,
      title: "Digital Banking Platform",
      client: "SecureBank",
      category: "fintech",
      tags: ["React Native", "Firebase", "Biometric Auth"],
      image: portfolioMobile1,
      description:
        "Next-generation mobile banking application with advanced security features and intuitive user interface.",
      challenge:
        "Outdated mobile app with security concerns and poor user adoption",
      solution:
        "Modern React Native app with biometric authentication and real-time transaction monitoring",
      results: {
        users: "250% increase in app users",
        satisfaction: "4.8/5 user satisfaction",
        security: "Zero security incidents",
        transactions: "70% of transactions via mobile",
      },
      duration: "6 months",
      teamSize: "12 developers",
      technologies: [
        "React Native",
        "Firebase",
        "Node.js",
        "MongoDB",
        "Biometric SDK",
      ],
      liveUrl: "https://app.securebank.com",
      year: "2024",
    },
    {
      id: 4,
      title: "Cryptocurrency Trading Platform",
      client: "CryptoTrade Exchange",
      category: "fintech",
      tags: ["Vue.js", "Python", "Redis", "Blockchain"],
      image: portfolioAI1,
      description:
        "High-frequency cryptocurrency trading platform with real-time market data and automated trading algorithms.",
      challenge:
        "Need for ultra-low latency trading with secure cryptocurrency transactions",
      solution:
        "Microservices architecture with real-time data streaming and blockchain integration",
      results: {
        latency: "Sub-millisecond trade execution",
        volume: "$50M+ daily trading volume",
        uptime: "99.99% platform uptime",
        users: "100K+ active traders",
      },
      duration: "10 months",
      teamSize: "18 developers",
      technologies: [
        "Vue.js",
        "Python",
        "Redis",
        "WebSocket",
        "Blockchain APIs",
      ],
      liveUrl: "https://cryptotrade.example.com",
      year: "2023",
    },
    {
      id: 5,
      title: "Insurance Claims Processing",
      client: "SafeGuard Insurance",
      category: "fintech",
      tags: ["Angular", "AI/ML", "PostgreSQL", "OCR"],
      image: portfolioEnterprise2,
      description:
        "AI-powered insurance claims processing system with automated document analysis and fraud detection.",
      challenge:
        "Manual claims processing causing delays and high operational costs",
      solution:
        "AI-driven automation with OCR for document processing and ML for fraud detection",
      results: {
        processing: "85% faster claims processing",
        accuracy: "95% automated decision accuracy",
        fraud: "60% reduction in fraudulent claims",
        costs: "40% reduction in processing costs",
      },
      duration: "7 months",
      teamSize: "14 developers",
      technologies: [
        "Angular",
        "Python",
        "TensorFlow",
        "PostgreSQL",
        "OCR APIs",
      ],
      liveUrl: "https://claims.safeguard.com",
      year: "2024",
    },

    // E-commerce & Retail
    {
      id: 6,
      title: "Multi-Vendor Marketplace",
      client: "ShopSmart Retail",
      category: "ecommerce",
      tags: ["React", "Node.js", "PostgreSQL", "Stripe"],
      image: portfolioWeb1,
      description:
        "Complete multi-vendor e-commerce marketplace with vendor management, payment processing, and logistics integration.",
      challenge:
        "Legacy platform with poor performance and limited vendor capabilities",
      solution:
        "Modern marketplace with advanced vendor tools and optimized user experience",
      results: {
        vendors: "80+ active vendors",
        performance: "300% faster page load times",
        conversion: "45% increase in conversion rate",
        revenue: "60% increase in platform revenue",
      },
      duration: "8 months",
      teamSize: "16 developers",
      technologies: [
        "React",
        "Node.js",
        "Express",
        "PostgreSQL",
        "Redis",
        "AWS",
      ],
      liveUrl: "https://marketplace.shopsmart.com",
      year: "2024",
    },
    {
      id: 7,
      title: "Fashion Retail Analytics",
      client: "TrendStyle Fashion",
      category: "retail",
      tags: ["Python", "AI/ML", "React", "BigQuery"],
      image: portfolioAI2,
      description:
        "AI-powered fashion retail analytics platform for trend prediction and inventory optimization.",
      challenge:
        "Overstocking and understocking issues due to unpredictable fashion trends",
      solution:
        "Machine learning models for trend prediction and demand forecasting",
      results: {
        accuracy: "88% trend prediction accuracy",
        inventory: "35% reduction in excess inventory",
        sales: "25% increase in sales",
        waste: "50% reduction in unsold items",
      },
      duration: "5 months",
      teamSize: "10 developers",
      technologies: [
        "Python",
        "TensorFlow",
        "React",
        "BigQuery",
        "Fashion APIs",
      ],
      liveUrl: "https://analytics.trendstyle.com",
      year: "2024",
    },

    // Manufacturing & Industry 4.0
    {
      id: 8,
      title: "Smart Factory Management",
      client: "AutoManu Industries",
      category: "manufacturing",
      tags: ["IoT", "Python", "InfluxDB", "Grafana"],
      image: portfolioEnterprise1,
      description:
        "Industry 4.0 smart factory management system with IoT integration and predictive maintenance.",
      challenge: "Unplanned downtime and inefficient production processes",
      solution:
        "IoT-enabled monitoring with predictive maintenance and real-time analytics",
      results: {
        downtime: "70% reduction in unplanned downtime",
        efficiency: "45% improvement in production efficiency",
        maintenance: "60% reduction in maintenance costs",
        quality: "30% improvement in product quality",
      },
      duration: "9 months",
      teamSize: "20 developers",
      technologies: [
        "Python",
        "IoT Sensors",
        "InfluxDB",
        "Grafana",
        "Machine Learning",
      ],
      liveUrl: "https://factory.automanu.com",
      year: "2023",
    },
    {
      id: 9,
      title: "Supply Chain Visibility Platform",
      client: "LogiFlow Industries",
      category: "logistics",
      tags: ["Vue.js", "Python", "PostgreSQL", "Blockchain"],
      image: portfolioEnterprise2,
      description:
        "Blockchain-enabled supply chain management platform with end-to-end traceability.",
      challenge: "Lack of supply chain transparency and traceability issues",
      solution:
        "Blockchain-based platform with IoT integration for complete visibility",
      results: {
        visibility: "100% supply chain visibility",
        traceability: "End-to-end product traceability",
        costs: "25% reduction in operational costs",
        compliance: "100% regulatory compliance",
      },
      duration: "7 months",
      teamSize: "14 developers",
      technologies: ["Vue.js", "Python", "Blockchain", "PostgreSQL", "IoT"],
      liveUrl: "https://supply.logiflow.com",
      year: "2023",
    },

    // Education & EdTech
    {
      id: 10,
      title: "Virtual Learning Platform",
      client: "EduConnect Global",
      category: "education",
      tags: ["Next.js", "WebRTC", "MongoDB", "AI"],
      image: portfolioWeb2,
      description:
        "Comprehensive virtual learning platform with AI-powered personalized learning paths.",
      challenge:
        "One-size-fits-all approach not meeting diverse learning needs",
      solution:
        "AI-driven personalized learning with adaptive content delivery",
      results: {
        engagement: "150% increase in student engagement",
        completion: "80% course completion rate",
        learning: "65% improvement in learning outcomes",
        retention: "75% knowledge retention rate",
      },
      duration: "6 months",
      teamSize: "11 developers",
      technologies: ["Next.js", "AI/ML", "WebRTC", "MongoDB", "Redis"],
      liveUrl: "https://learn.educonnect.com",
      year: "2024",
    },
    {
      id: 11,
      title: "University Management System",
      client: "Metropolitan University",
      category: "education",
      tags: ["Angular", "Java", "MySQL", "Analytics"],
      image: portfolioEnterprise1,
      description:
        "Complete university management system with student information, course management, and analytics.",
      challenge:
        "Fragmented systems across different departments causing data silos",
      solution:
        "Integrated platform with unified data management and analytics",
      results: {
        efficiency: "60% improvement in administrative efficiency",
        satisfaction: "90% student satisfaction rate",
        data: "Unified data across all departments",
        reporting: "Real-time analytics and reporting",
      },
      duration: "12 months",
      teamSize: "25 developers",
      technologies: [
        "Angular",
        "Spring Boot",
        "MySQL",
        "Apache Kafka",
        "Elasticsearch",
      ],
      liveUrl: "https://portal.metuniversity.edu",
      year: "2023",
    },

    // Automotive & Transportation
    {
      id: 12,
      title: "Connected Vehicle Platform",
      client: "AutoTech Motors",
      category: "industrial",
      tags: ["IoT", "React", "AWS", "ML"],
      image: portfolioAI1,
      description:
        "Connected vehicle platform with telematics, predictive maintenance, and fleet management.",
      challenge:
        "Limited visibility into vehicle performance and maintenance needs",
      solution:
        "IoT-enabled telematics with predictive analytics and real-time monitoring",
      results: {
        maintenance: "50% reduction in unexpected breakdowns",
        efficiency: "30% improvement in fuel efficiency",
        safety: "40% reduction in accidents",
        satisfaction: "95% customer satisfaction",
      },
      duration: "8 months",
      teamSize: "16 developers",
      technologies: [
        "React",
        "IoT",
        "AWS IoT",
        "Machine Learning",
        "Real-time Analytics",
      ],
      liveUrl: "https://connected.autotech.com",
      year: "2024",
    },
    {
      id: 13,
      title: "Ride-Sharing Platform",
      client: "CityRide",
      category: "transportation",
      tags: ["React Native", "Node.js", "Redis", "GPS"],
      image: portfolioMobile2,
      description:
        "Advanced ride-sharing platform with real-time matching, route optimization, and payment integration.",
      challenge:
        "Inefficient driver-passenger matching and poor route optimization",
      solution:
        "AI-powered matching algorithm with real-time traffic data integration",
      results: {
        matching: "90% faster driver-passenger matching",
        routes: "25% shorter average trip time",
        satisfaction: "4.8/5 user satisfaction",
        revenue: "150% increase in platform revenue",
      },
      duration: "10 months",
      teamSize: "22 developers",
      technologies: [
        "React Native",
        "Node.js",
        "Redis",
        "GPS APIs",
        "Payment Gateways",
      ],
      liveUrl: "https://app.cityride.com",
      year: "2023",
    },

    // Energy & Utilities
    {
      id: 14,
      title: "Smart Grid Management",
      client: "PowerGrid Solutions",
      category: "energy",
      tags: ["Python", "IoT", "InfluxDB", "React"],
      image: portfolioEnterprise2,
      description:
        "Smart grid management system with renewable energy integration and demand response optimization.",
      challenge:
        "Inefficient energy distribution and limited renewable energy integration",
      solution:
        "AI-powered grid optimization with real-time demand response management",
      results: {
        efficiency: "35% improvement in grid efficiency",
        renewable: "60% increase in renewable energy usage",
        costs: "20% reduction in operational costs",
        reliability: "99.9% grid reliability",
      },
      duration: "14 months",
      teamSize: "30 developers",
      technologies: ["Python", "IoT", "InfluxDB", "React", "Machine Learning"],
      liveUrl: "https://grid.powersolutions.com",
      year: "2023",
    },
    {
      id: 15,
      title: "Solar Farm Monitoring",
      client: "SolarMax Energy",
      category: "energy",
      tags: ["Vue.js", "Python", "TimescaleDB", "IoT"],
      image: portfolioAI2,
      description:
        "Comprehensive solar farm monitoring and optimization platform with predictive maintenance.",
      challenge:
        "Suboptimal solar panel performance and reactive maintenance approach",
      solution: "IoT-based monitoring with AI-powered performance optimization",
      results: {
        performance: "25% increase in energy generation",
        maintenance: "50% reduction in maintenance costs",
        efficiency: "Real-time performance optimization",
        roi: "30% faster ROI achievement",
      },
      duration: "6 months",
      teamSize: "12 developers",
      technologies: ["Vue.js", "Python", "TimescaleDB", "IoT Sensors", "ML"],
      liveUrl: "https://monitor.solarmax.com",
      year: "2024",
    },

    // Telecommunications
    {
      id: 16,
      title: "5G Network Optimization",
      client: "TelecomPro",
      category: "telecom",
      tags: ["Python", "5G", "AI/ML", "Kubernetes"],
      image: portfolioEnterprise1,
      description:
        "AI-powered 5G network optimization platform for enhanced performance and reduced latency.",
      challenge: "Suboptimal 5G network performance and high operational costs",
      solution:
        "ML-based network optimization with automated resource allocation",
      results: {
        latency: "40% reduction in network latency",
        throughput: "60% increase in data throughput",
        costs: "30% reduction in operational costs",
        coverage: "25% improvement in coverage quality",
      },
      duration: "11 months",
      teamSize: "28 developers",
      technologies: [
        "Python",
        "Kubernetes",
        "5G APIs",
        "Machine Learning",
        "Network Analytics",
      ],
      liveUrl: "https://optimize.telecompro.com",
      year: "2024",
    },
    {
      id: 17,
      title: "Customer Support Portal",
      client: "ConnectTel",
      category: "telecom",
      tags: ["React", "AI Chatbot", "Node.js", "MongoDB"],
      image: portfolioWeb1,
      description:
        "AI-powered customer support portal with intelligent chatbot and self-service capabilities.",
      challenge: "High customer support costs and long resolution times",
      solution:
        "AI chatbot with natural language processing and automated issue resolution",
      results: {
        resolution: "70% of issues resolved automatically",
        satisfaction: "4.7/5 customer satisfaction",
        costs: "50% reduction in support costs",
        response: "90% faster initial response time",
      },
      duration: "5 months",
      teamSize: "10 developers",
      technologies: [
        "React",
        "Node.js",
        "MongoDB",
        "AI/NLP",
        "Chatbot Framework",
      ],
      liveUrl: "https://support.connecttel.com",
      year: "2024",
    },

    // Media & Entertainment
    {
      id: 18,
      title: "Video Streaming Platform",
      client: "StreamMax Entertainment",
      category: "media",
      tags: ["React", "CDN", "AWS", "Video Analytics"],
      image: portfolioWeb2,
      description:
        "Scalable video streaming platform with adaptive bitrate streaming and content analytics.",
      challenge:
        "Poor video quality and buffering issues affecting user experience",
      solution:
        "CDN-optimized streaming with adaptive bitrate and global edge distribution",
      results: {
        quality: "95% reduction in buffering issues",
        users: "2M+ concurrent users supported",
        engagement: "40% increase in viewing time",
        global: "Available in 50+ countries",
      },
      duration: "9 months",
      teamSize: "18 developers",
      technologies: [
        "React",
        "AWS MediaLive",
        "CDN",
        "Video Analytics",
        "Adaptive Streaming",
      ],
      liveUrl: "https://stream.streammax.com",
      year: "2023",
    },
    {
      id: 19,
      title: "Music Distribution Platform",
      client: "SoundWave Records",
      category: "media",
      tags: ["Vue.js", "Python", "PostgreSQL", "Audio"],
      image: portfolioAI1,
      description:
        "Digital music distribution and royalty management platform for independent artists.",
      challenge:
        "Complex royalty calculations and limited distribution reach for indie artists",
      solution:
        "Automated royalty system with global streaming platform integration",
      results: {
        artists: "10K+ independent artists onboarded",
        distribution: "Distribution to 200+ platforms",
        royalties: "Automated real-time royalty calculations",
        revenue: "300% increase in artist revenue",
      },
      duration: "7 months",
      teamSize: "14 developers",
      technologies: [
        "Vue.js",
        "Python",
        "PostgreSQL",
        "Audio Processing",
        "API Integrations",
      ],
      liveUrl: "https://distribute.soundwave.com",
      year: "2024",
    },

    // Real Estate & PropTech
    {
      id: 20,
      title: "Virtual Property Tours",
      client: "RealEstate Pro",
      category: "real-estate",
      tags: ["React", "VR/AR", "3D", "WebGL"],
      image: portfolioWeb1,
      description:
        "Immersive virtual property tour platform with VR/AR capabilities and 3D visualization.",
      challenge:
        "Limited property viewing options during pandemic and for remote buyers",
      solution:
        "VR/AR-enabled virtual tours with interactive 3D property exploration",
      results: {
        tours: "500% increase in virtual property tours",
        sales: "30% faster property sales cycle",
        reach: "Global buyer accessibility",
        satisfaction: "4.9/5 buyer satisfaction",
      },
      duration: "8 months",
      teamSize: "15 developers",
      technologies: [
        "React",
        "WebGL",
        "VR/AR SDKs",
        "3D Modeling",
        "Cloud Storage",
      ],
      liveUrl: "https://tours.realestatepro.com",
      year: "2024",
    },
    {
      id: 21,
      title: "Property Management System",
      client: "PropManager Solutions",
      category: "real-estate",
      tags: ["Angular", "Java", "MySQL", "Mobile"],
      image: portfolioEnterprise2,
      description:
        "Comprehensive property management system with tenant portal and maintenance tracking.",
      challenge: "Manual property management processes causing inefficiencies",
      solution:
        "Integrated platform with tenant self-service and automated workflows",
      results: {
        efficiency: "60% improvement in management efficiency",
        satisfaction: "85% tenant satisfaction rate",
        maintenance: "50% faster maintenance response",
        occupancy: "95% average occupancy rate",
      },
      duration: "10 months",
      teamSize: "20 developers",
      technologies: [
        "Angular",
        "Spring Boot",
        "MySQL",
        "React Native",
        "Payment APIs",
      ],
      liveUrl: "https://manage.propmanager.com",
      year: "2023",
    },

    // Agriculture & AgriTech
    {
      id: 22,
      title: "Precision Agriculture Platform",
      client: "FarmTech Solutions",
      category: "agriculture",
      tags: ["IoT", "Python", "ML", "Drone Integration"],
      image: portfolioAI2,
      description:
        "IoT-enabled precision agriculture platform with drone integration and crop analytics.",
      challenge:
        "Inefficient crop monitoring and resource utilization in large farms",
      solution:
        "IoT sensors and drone data integration with AI-powered crop analytics",
      results: {
        yield: "35% increase in crop yield",
        water: "40% reduction in water usage",
        pesticides: "50% reduction in pesticide usage",
        roi: "25% improvement in farm ROI",
      },
      duration: "8 months",
      teamSize: "16 developers",
      technologies: [
        "Python",
        "IoT",
        "Machine Learning",
        "Drone APIs",
        "Agricultural Sensors",
      ],
      liveUrl: "https://precision.farmtech.com",
      year: "2024",
    },

    // Food & Beverage
    {
      id: 23,
      title: "Restaurant Chain Management",
      client: "FoodChain Enterprises",
      category: "food-beverage",
      tags: ["React", "Node.js", "POS Integration", "Analytics"],
      image: portfolioEnterprise1,
      description:
        "Integrated restaurant chain management system with POS integration and inventory tracking.",
      challenge: "Fragmented operations across multiple restaurant locations",
      solution:
        "Centralized management platform with real-time data synchronization",
      results: {
        efficiency: "45% improvement in operational efficiency",
        waste: "30% reduction in food waste",
        sales: "20% increase in average sales",
        inventory: "Real-time inventory tracking across all locations",
      },
      duration: "9 months",
      teamSize: "18 developers",
      technologies: [
        "React",
        "Node.js",
        "POS APIs",
        "Analytics",
        "Inventory Management",
      ],
      liveUrl: "https://manage.foodchain.com",
      year: "2023",
    },

    // Gaming & Interactive Media
    {
      id: 24,
      title: "Multiplayer Gaming Platform",
      client: "GameStudio Interactive",
      category: "gaming",
      tags: ["Unity", "C#", "WebSocket", "Cloud Gaming"],
      image: portfolioWeb2,
      description:
        "Cloud-based multiplayer gaming platform with real-time synchronization and social features.",
      challenge: "High latency and synchronization issues in multiplayer games",
      solution:
        "Optimized network architecture with edge computing and advanced algorithms",
      results: {
        latency: "75% reduction in game latency",
        players: "100K+ concurrent players supported",
        uptime: "99.9% platform uptime",
        engagement: "3+ hours average session time",
      },
      duration: "12 months",
      teamSize: "25 developers",
      technologies: [
        "Unity",
        "C#",
        "WebSocket",
        "Redis",
        "Cloud Infrastructure",
      ],
      liveUrl: "https://play.gamestudio.com",
      year: "2024",
    },

    // Sports & Fitness
    {
      id: 25,
      title: "Sports Analytics Platform",
      client: "ProSports Analytics",
      category: "sports",
      tags: ["Python", "Computer Vision", "ML", "React"],
      image: portfolioAI1,
      description:
        "AI-powered sports analytics platform with computer vision for performance analysis.",
      challenge:
        "Manual sports performance analysis limiting coaching effectiveness",
      solution:
        "Computer vision and ML for automated performance metrics and insights",
      results: {
        accuracy: "95% accuracy in performance metrics",
        analysis: "Real-time game analysis",
        improvement: "30% improvement in player performance",
        teams: "50+ professional teams using platform",
      },
      duration: "10 months",
      teamSize: "20 developers",
      technologies: [
        "Python",
        "Computer Vision",
        "TensorFlow",
        "React",
        "Video Processing",
      ],
      liveUrl: "https://analytics.prosports.com",
      year: "2023",
    },

    // Legal & LegalTech
    {
      id: 26,
      title: "Legal Case Management",
      client: "LawFirm Partners",
      category: "legal",
      tags: ["Angular", "Document AI", "Java", "Security"],
      image: portfolioEnterprise2,
      description:
        "Comprehensive legal case management system with document AI and client portal.",
      challenge: "Manual document processing and case tracking inefficiencies",
      solution:
        "AI-powered document analysis with automated case workflow management",
      results: {
        processing: "80% faster document processing",
        accuracy: "95% document classification accuracy",
        billable: "40% increase in billable hours",
        satisfaction: "90% client satisfaction rate",
      },
      duration: "11 months",
      teamSize: "22 developers",
      technologies: [
        "Angular",
        "Document AI",
        "Java",
        "Security Framework",
        "OCR",
      ],
      liveUrl: "https://cases.lawfirmpartners.com",
      year: "2024",
    },

    // Travel & Hospitality
    {
      id: 27,
      title: "Hotel Booking Platform",
      client: "StayEasy Hotels",
      category: "hospitality",
      tags: ["React", "Microservices", "Payment", "Mobile"],
      image: portfolioWeb1,
      description:
        "Modern hotel booking platform with dynamic pricing and personalized recommendations.",
      challenge:
        "Low conversion rates and poor user experience on booking platform",
      solution:
        "AI-powered recommendations with optimized booking flow and mobile-first design",
      results: {
        conversion: "65% increase in booking conversion",
        revenue: "45% increase in revenue per visitor",
        mobile: "80% of bookings from mobile devices",
        satisfaction: "4.8/5 customer satisfaction",
      },
      duration: "7 months",
      teamSize: "14 developers",
      technologies: [
        "React",
        "Microservices",
        "Payment APIs",
        "React Native",
        "AI/ML",
      ],
      liveUrl: "https://book.stayeasy.com",
      year: "2024",
    },

    // Logistics & Delivery
    {
      id: 28,
      title: "Last-Mile Delivery Platform",
      client: "QuickDeliver Logistics",
      category: "logistics",
      tags: ["Flutter", "Route Optimization", "IoT", "Analytics"],
      image: portfolioMobile2,
      description:
        "Advanced last-mile delivery platform with real-time tracking and route optimization.",
      challenge: "Inefficient delivery routes and poor customer visibility",
      solution:
        "AI-powered route optimization with real-time tracking and notifications",
      results: {
        efficiency: "40% improvement in delivery efficiency",
        time: "30% reduction in average delivery time",
        satisfaction: "95% customer satisfaction",
        costs: "25% reduction in delivery costs",
      },
      duration: "8 months",
      teamSize: "16 developers",
      technologies: [
        "Flutter",
        "Route Optimization APIs",
        "IoT",
        "Real-time Analytics",
      ],
      liveUrl: "https://track.quickdeliver.com",
      year: "2023",
    },

    // Construction & Architecture
    {
      id: 29,
      title: "Construction Project Management",
      client: "BuildPro Construction",
      category: "construction",
      tags: ["React", "CAD Integration", "Mobile", "IoT"],
      image: portfolioEnterprise1,
      description:
        "Comprehensive construction project management platform with CAD integration and progress tracking.",
      challenge:
        "Poor project visibility and coordination among construction teams",
      solution:
        "Integrated platform with real-time progress tracking and collaboration tools",
      results: {
        completion: "20% faster project completion",
        costs: "15% reduction in project costs",
        quality: "30% improvement in quality metrics",
        safety: "50% reduction in safety incidents",
      },
      duration: "10 months",
      teamSize: "19 developers",
      technologies: [
        "React",
        "CAD APIs",
        "React Native",
        "IoT",
        "Project Management",
      ],
      liveUrl: "https://manage.buildpro.com",
      year: "2024",
    },

    // Fashion & Apparel
    {
      id: 30,
      title: "Fashion Design Platform",
      client: "DesignHub Fashion",
      category: "fashion",
      tags: ["Vue.js", "3D Modeling", "AR", "E-commerce"],
      image: portfolioAI2,
      description:
        "3D fashion design platform with AR try-on capabilities and direct-to-consumer sales.",
      challenge: "High return rates due to poor fit and design visualization",
      solution:
        "3D modeling and AR try-on technology with size recommendation engine",
      results: {
        returns: "60% reduction in product returns",
        engagement: "200% increase in user engagement",
        sales: "35% increase in conversion rate",
        satisfaction: "4.9/5 customer satisfaction",
      },
      duration: "9 months",
      teamSize: "17 developers",
      technologies: [
        "Vue.js",
        "3D Modeling",
        "AR SDKs",
        "E-commerce APIs",
        "Size APIs",
      ],
      liveUrl: "https://design.designhub.com",
      year: "2024",
    },

    // Environmental & Sustainability
    {
      id: 31,
      title: "Carbon Footprint Tracking",
      client: "EcoTrack Solutions",
      category: "environmental",
      tags: ["Python", "IoT", "Blockchain", "Analytics"],
      image: portfolioEnterprise2,
      description:
        "Enterprise carbon footprint tracking platform with blockchain verification and sustainability analytics.",
      challenge:
        "Difficulty in accurate carbon footprint measurement and verification",
      solution:
        "IoT-enabled tracking with blockchain verification and AI-powered analytics",
      results: {
        accuracy: "98% accuracy in carbon tracking",
        reduction: "Average 30% carbon footprint reduction",
        compliance: "100% regulatory compliance",
        reporting: "Automated sustainability reporting",
      },
      duration: "8 months",
      teamSize: "15 developers",
      technologies: [
        "Python",
        "IoT",
        "Blockchain",
        "Analytics",
        "Environmental APIs",
      ],
      liveUrl: "https://track.ecotrack.com",
      year: "2024",
    },

    // Non-Profit & Social Impact
    {
      id: 32,
      title: "Donation Management Platform",
      client: "GlobalGive Foundation",
      category: "non-profit",
      tags: ["React", "Payment", "CRM", "Analytics"],
      image: portfolioWeb1,
      description:
        "Comprehensive donation management platform with donor CRM and impact tracking.",
      challenge: "Fragmented donation processes and poor donor engagement",
      solution:
        "Integrated platform with automated donor management and impact visualization",
      results: {
        donations: "150% increase in online donations",
        retention: "80% donor retention rate",
        efficiency: "60% reduction in administrative costs",
        impact: "Real-time impact tracking and reporting",
      },
      duration: "6 months",
      teamSize: "12 developers",
      technologies: [
        "React",
        "Payment APIs",
        "CRM Integration",
        "Analytics",
        "Email Marketing",
      ],
      liveUrl: "https://donate.globalgive.org",
      year: "2023",
    },

    // Government & Public Sector
    {
      id: 33,
      title: "Citizen Services Portal",
      client: "City Government",
      category: "government",
      tags: ["Angular", "Security", "Accessibility", "Integration"],
      image: portfolioEnterprise1,
      description:
        "Digital citizen services portal with online applications and service tracking.",
      challenge:
        "Long queues and inefficient manual processes for government services",
      solution:
        "Digital-first approach with online applications and automated workflows",
      results: {
        efficiency: "70% reduction in processing time",
        satisfaction: "4.6/5 citizen satisfaction",
        cost: "40% reduction in operational costs",
        accessibility: "WCAG 2.1 AA compliance",
      },
      duration: "12 months",
      teamSize: "24 developers",
      technologies: [
        "Angular",
        "Security Framework",
        "Accessibility",
        "Legacy Integration",
      ],
      liveUrl: "https://services.cityportal.gov",
      year: "2023",
    },

    // Pharmaceutical & Biotech
    {
      id: 34,
      title: "Clinical Trial Management",
      client: "BioPharma Research",
      category: "pharmaceutical",
      tags: ["React", "Regulatory", "Security", "Analytics"],
      image: portfolioAI1,
      description:
        "Clinical trial management system with regulatory compliance and data analytics.",
      challenge:
        "Complex regulatory requirements and data management in clinical trials",
      solution: "Compliance-first platform with automated regulatory reporting",
      results: {
        compliance: "100% regulatory compliance",
        efficiency: "50% faster trial completion",
        quality: "95% data quality score",
        cost: "35% reduction in trial costs",
      },
      duration: "14 months",
      teamSize: "28 developers",
      technologies: [
        "React",
        "Regulatory APIs",
        "Security",
        "Clinical Analytics",
        "CDISC",
      ],
      liveUrl: "https://trials.biopharma.com",
      year: "2024",
    },

    // Aerospace & Defense
    {
      id: 35,
      title: "Flight Operations Management",
      client: "AeroTech Systems",
      category: "aerospace",
      tags: ["Java", "Real-time", "Security", "Integration"],
      image: portfolioEnterprise2,
      description:
        "Mission-critical flight operations management system with real-time monitoring.",
      challenge:
        "Need for real-time flight data processing with high security requirements",
      solution:
        "High-availability system with real-time data processing and advanced security",
      results: {
        reliability: "99.99% system uptime",
        processing: "Real-time data processing",
        security: "Military-grade security",
        operations: "80+ flights managed daily",
      },
      duration: "16 months",
      teamSize: "32 developers",
      technologies: [
        "Java",
        "Real-time Systems",
        "Security",
        "Aviation APIs",
        "Monitoring",
      ],
      liveUrl: "https://ops.aerotech.com",
      year: "2023",
    },

    // Maritime & Shipping
    {
      id: 36,
      title: "Port Management System",
      client: "MarinePort Authority",
      category: "maritime",
      tags: ["Python", "IoT", "Optimization", "Analytics"],
      image: portfolioAI2,
      description:
        "Smart port management system with vessel tracking and cargo optimization.",
      challenge:
        "Inefficient port operations and poor cargo handling coordination",
      solution:
        "IoT-enabled port automation with AI-powered optimization algorithms",
      results: {
        throughput: "40% increase in port throughput",
        waiting: "60% reduction in vessel waiting time",
        efficiency: "50% improvement in cargo handling",
        costs: "25% reduction in operational costs",
      },
      duration: "11 months",
      teamSize: "21 developers",
      technologies: [
        "Python",
        "IoT",
        "Optimization Algorithms",
        "Maritime APIs",
        "Analytics",
      ],
      liveUrl: "https://port.marineauthority.com",
      year: "2024",
    },

    // Mining & Resources
    {
      id: 37,
      title: "Mining Operations Platform",
      client: "ResourceMax Mining",
      category: "mining",
      tags: ["IoT", "Safety", "Analytics", "Mobile"],
      image: portfolioEnterprise1,
      description:
        "Comprehensive mining operations platform with safety monitoring and resource optimization.",
      challenge:
        "Safety concerns and inefficient resource extraction processes",
      solution:
        "IoT-based safety monitoring with AI-powered resource optimization",
      results: {
        safety: "80% improvement in safety metrics",
        efficiency: "35% increase in extraction efficiency",
        costs: "20% reduction in operational costs",
        incidents: "90% reduction in safety incidents",
      },
      duration: "13 months",
      teamSize: "26 developers",
      technologies: [
        "IoT",
        "Safety Systems",
        "Analytics",
        "Mobile Apps",
        "Mining Equipment APIs",
      ],
      liveUrl: "https://ops.resourcemax.com",
      year: "2023",
    },

    // Insurance & InsurTech
    {
      id: 38,
      title: "Digital Insurance Platform",
      client: "InsureTech Pro",
      category: "insurance",
      tags: ["Vue.js", "AI Claims", "Mobile", "Integration"],
      image: portfolioWeb2,
      description:
        "Digital-first insurance platform with AI-powered claims processing and mobile apps.",
      challenge: "Slow claims processing and poor customer experience",
      solution:
        "AI-automated claims processing with mobile-first customer experience",
      results: {
        processing: "85% faster claims processing",
        automation: "70% of claims processed automatically",
        satisfaction: "4.7/5 customer satisfaction",
        costs: "45% reduction in processing costs",
      },
      duration: "9 months",
      teamSize: "18 developers",
      technologies: [
        "Vue.js",
        "AI/ML",
        "Mobile Apps",
        "Insurance APIs",
        "Payment Systems",
      ],
      liveUrl: "https://claims.insuretech.com",
      year: "2024",
    },

    // Security & Cybersecurity
    {
      id: 39,
      title: "Cybersecurity Monitoring Platform",
      client: "SecureGuard Systems",
      category: "cybersecurity",
      tags: ["Python", "AI Threat Detection", "Real-time", "SIEM"],
      image: portfolioAI1,
      description:
        "Advanced cybersecurity monitoring platform with AI-powered threat detection.",
      challenge: "Increasing cyber threats and slow incident response times",
      solution:
        "AI-powered threat detection with automated incident response workflows",
      results: {
        detection: "95% threat detection accuracy",
        response: "80% faster incident response",
        prevention: "99.5% attack prevention rate",
        compliance: "Full SOC 2 compliance",
      },
      duration: "10 months",
      teamSize: "20 developers",
      technologies: [
        "Python",
        "AI/ML",
        "SIEM",
        "Security APIs",
        "Threat Intelligence",
      ],
      liveUrl: "https://monitor.secureguard.com",
      year: "2024",
    },

    // Blockchain & Cryptocurrency
    {
      id: 40,
      title: "DeFi Lending Platform",
      client: "CryptoLend Finance",
      category: "blockchain",
      tags: ["Solidity", "Web3", "React", "DeFi"],
      image: portfolioEnterprise2,
      description:
        "Decentralized lending platform with automated market making and yield farming.",
      challenge:
        "Complex DeFi protocols and poor user experience for non-technical users",
      solution:
        "User-friendly interface with automated DeFi strategies and risk management",
      results: {
        tvl: "$50M+ Total Value Locked",
        users: "25K+ active users",
        yield: "Average 12% APY for users",
        security: "Zero security incidents",
      },
      duration: "8 months",
      teamSize: "15 developers",
      technologies: [
        "Solidity",
        "Web3.js",
        "React",
        "DeFi Protocols",
        "Smart Contracts",
      ],
      liveUrl: "https://lend.cryptolend.com",
      year: "2024",
    },

    // IoT & Smart Cities
    {
      id: 41,
      title: "Smart City Infrastructure",
      client: "UrbanTech Solutions",
      category: "smart-city",
      tags: ["IoT", "Edge Computing", "Analytics", "5G"],
      image: portfolioAI2,
      description:
        "Comprehensive smart city platform with IoT sensors and edge computing for urban management.",
      challenge: "Inefficient city services and lack of real-time urban data",
      solution:
        "IoT-enabled city infrastructure with edge computing and real-time analytics",
      results: {
        efficiency: "45% improvement in city services efficiency",
        energy: "30% reduction in energy consumption",
        traffic: "25% improvement in traffic flow",
        quality: "Real-time air quality monitoring",
      },
      duration: "18 months",
      teamSize: "35 developers",
      technologies: [
        "IoT",
        "Edge Computing",
        "5G",
        "Analytics",
        "Urban Planning APIs",
      ],
      liveUrl: "https://city.urbantech.com",
      year: "2023",
    },

    // Wellness & Mental Health
    {
      id: 42,
      title: "Mental Health Platform",
      client: "MindCare Wellness",
      category: "wellness",
      tags: ["React Native", "AI Therapy", "Security", "Telehealth"],
      image: portfolioMobile1,
      description:
        "Digital mental health platform with AI-powered therapy assistance and telehealth integration.",
      challenge:
        "Limited access to mental health services and stigma around seeking help",
      solution:
        "Anonymous, accessible platform with AI-assisted therapy and professional support",
      results: {
        users: "100K+ users supported",
        satisfaction: "4.8/5 user satisfaction",
        accessibility: "24/7 platform availability",
        outcomes: "85% improvement in mental health scores",
      },
      duration: "7 months",
      teamSize: "13 developers",
      technologies: [
        "React Native",
        "AI/NLP",
        "HIPAA Security",
        "Telehealth APIs",
        "Analytics",
      ],
      liveUrl: "https://app.mindcare.com",
      year: "2024",
    },

    // Pet Care & Veterinary
    {
      id: 43,
      title: "Veterinary Practice Management",
      client: "VetCare Solutions",
      category: "veterinary",
      tags: ["Angular", "Mobile", "Scheduling", "Inventory"],
      image: portfolioWeb1,
      description:
        "Complete veterinary practice management system with appointment scheduling and medical records.",
      challenge:
        "Manual processes and poor client communication in veterinary practices",
      solution:
        "Digital practice management with client portal and automated communications",
      results: {
        efficiency: "55% improvement in practice efficiency",
        satisfaction: "92% client satisfaction",
        revenue: "30% increase in practice revenue",
        communication: "Automated appointment reminders and follow-ups",
      },
      duration: "8 months",
      teamSize: "14 developers",
      technologies: [
        "Angular",
        "Mobile Apps",
        "Scheduling APIs",
        "Inventory Management",
        "Payment",
      ],
      liveUrl: "https://practice.vetcare.com",
      year: "2023",
    },

    // Beauty & Cosmetics
    {
      id: 44,
      title: "Beauty Consultation Platform",
      client: "GlowBeauty",
      category: "beauty",
      tags: ["AI Beauty Analysis", "AR", "E-commerce", "Mobile"],
      image: portfolioAI1,
      description:
        "AI-powered beauty consultation platform with virtual try-on and personalized recommendations.",
      challenge:
        "Difficulty in choosing right beauty products without physical testing",
      solution:
        "AI beauty analysis with AR try-on and personalized product recommendations",
      results: {
        engagement: "300% increase in user engagement",
        conversion: "75% increase in product sales",
        returns: "50% reduction in product returns",
        satisfaction: "4.9/5 customer satisfaction",
      },
      duration: "6 months",
      teamSize: "12 developers",
      technologies: [
        "AI/Computer Vision",
        "AR SDKs",
        "E-commerce",
        "Mobile Apps",
        "Beauty APIs",
      ],
      liveUrl: "https://consult.glowbeauty.com",
      year: "2024",
    },

    // Fitness & Sports Tech
    {
      id: 45,
      title: "Smart Gym Management",
      client: "FitTech Gyms",
      category: "fitness",
      tags: ["IoT", "Wearables", "Analytics", "Mobile"],
      image: portfolioMobile2,
      description:
        "Smart gym management platform with IoT equipment integration and member analytics.",
      challenge:
        "Poor equipment utilization and limited member engagement insights",
      solution:
        "IoT-enabled equipment monitoring with member analytics and personalized training",
      results: {
        utilization: "60% improvement in equipment utilization",
        retention: "40% increase in member retention",
        revenue: "35% increase in gym revenue",
        satisfaction: "4.8/5 member satisfaction",
      },
      duration: "7 months",
      teamSize: "15 developers",
      technologies: [
        "IoT",
        "Wearable APIs",
        "Analytics",
        "Mobile Apps",
        "Fitness Equipment APIs",
      ],
      liveUrl: "https://manage.fittech.com",
      year: "2024",
    },
  ];

  const categories = [
    { value: "all", label: "All Projects" },
    { value: "healthcare", label: "Healthcare" },
    { value: "fintech", label: "FinTech & BFSI" },
    { value: "ecommerce", label: "E-commerce" },
    { value: "retail", label: "Retail" },
    { value: "industrial", label: "Manufacturing & Automotive" },
    { value: "logistics", label: "Logistics" },
    { value: "education", label: "Education" },
    // { value: "automotive", label: "Automotive" },
    { value: "energy", label: "Energy" },
    { value: "telecom", label: "Telecommunications" },
    { value: "media", label: "Media" },
    { value: "real-estate", label: "Real Estate" },
    { value: "agriculture", label: "Agriculture" },
    { value: "food-beverage", label: "Food & Beverage" },
    { value: "gaming", label: "Gaming" },
    { value: "sports", label: "Sports" },
    { value: "legal", label: "Legal" },
    { value: "hospitality", label: "Hospitality" },
    { value: "construction", label: "Construction" },
    { value: "fashion", label: "Fashion" },
    { value: "environmental", label: "Environmental" },
    { value: "non-profit", label: "Non-Profit" },
    { value: "government", label: "Government" },
    { value: "pharmaceutical", label: "Pharmaceutical" },
    { value: "aerospace", label: "Aerospace" },
    { value: "maritime", label: "Maritime" },
    { value: "mining", label: "Mining" },
    { value: "insurance", label: "Insurance" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "blockchain", label: "Blockchain" },
    { value: "smart-city", label: "Smart City" },
    { value: "wellness", label: "Wellness" },
    { value: "veterinary", label: "Veterinary" },
    { value: "beauty", label: "Beauty" },
    { value: "fitness", label: "Fitness" },
  ];

  const filteredProjects =
    selectedCategory === "all"
      ? portfolioProjects
      : portfolioProjects.filter(
          (project) => project.category === selectedCategory,
        );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Briefcase className="mr-2 h-4 w-4" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-center">
            Our Portfolio
          </DialogTitle>
          <p className="text-muted-foreground text-center text-lg">
            Showcasing our successful projects and digital transformations
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Filter */}
          <div className="flex justify-center">
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Portfolio Grid */}
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                onClick={() => setSelectedProject(project.id)}
              >
                <div className="relative overflow-hidden">
                  <OptimizedImage
                    src={project.image}
                    alt={project.title}
                    width={400}
                    height={200}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge
                      variant="secondary"
                      className="bg-white/90 text-black"
                    >
                      {project.year}
                    </Badge>
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge
                      variant="outline"
                      className="bg-white/90 text-black border-white"
                    >
                      {project.category.replace("-", " ").toUpperCase()}
                    </Badge>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <Button size="sm" className="w-full">
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>

                <CardHeader>
                  <CardTitle className="text-lg">{project.title}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {project.client}
                  </p>
                </CardHeader>

                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {project.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="font-bold text-lg text-primary">
                        {project.duration}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Duration
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg text-primary">
                        {project.teamSize}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Team Size
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {project.tags.slice(0, 3).map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {project.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{project.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Project Modal */}
          {selectedProject && (
            <Dialog
              open={!!selectedProject}
              onOpenChange={() => setSelectedProject(null)}
            >
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                {(() => {
                  const project = portfolioProjects.find(
                    (p) => p.id === selectedProject,
                  );
                  if (!project) return null;

                  return (
                    <>
                      <DialogHeader>
                        <DialogTitle className="text-2xl">
                          {project.title}
                        </DialogTitle>
                        <div className="flex items-center gap-4">
                          <Badge>{project.category.replace("-", " ")}</Badge>
                          <span className="text-muted-foreground">
                            Client: {project.client}
                          </span>
                          <span className="text-muted-foreground">
                            Year: {project.year}
                          </span>
                        </div>
                      </DialogHeader>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-6">
                          <OptimizedImage
                            src={project.image}
                            alt={project.title}
                            width={500}
                            height={300}
                            className="w-full h-64 object-cover rounded-lg"
                          />

                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <Briefcase className="h-4 w-4 text-primary" />
                              Project Overview
                            </h4>
                            <p className="text-muted-foreground">
                              {project.description}
                            </p>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <Code className="h-4 w-4 text-secondary" />
                              Technologies Used
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {project.technologies.map((tech, i) => (
                                <Badge key={i} variant="outline">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Duration
                              </h4>
                              <p className="text-muted-foreground">
                                {project.duration}
                              </p>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Team Size
                              </h4>
                              <p className="text-muted-foreground">
                                {project.teamSize}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div>
                            <h4 className="font-semibold mb-4 flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-primary" />
                              Key Results
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              {Object.entries(project.results).map(
                                ([key, value]) => (
                                  <div
                                    key={key}
                                    className="text-center p-3 bg-gradient-subtle rounded-lg"
                                  >
                                    <div className="font-bold text-lg text-primary">
                                      {value}
                                    </div>
                                    <div className="text-xs text-muted-foreground capitalize">
                                      {key.replace(/([A-Z])/g, " $1")}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <Award className="h-4 w-4 text-secondary" />
                              Challenge
                            </h4>
                            <p className="text-muted-foreground mb-4">
                              {project.challenge}
                            </p>

                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-secondary" />
                              Solution
                            </h4>
                            <p className="text-muted-foreground">
                              {project.solution}
                            </p>
                          </div>

                          <div className="flex gap-4">
                            <Button
                              className="flex-1"
                              onClick={() => {
                                // Show notification instead of navigating to potentially non-existent URLs
                                alert(
                                  `This is a portfolio showcase. The project "${project.title}" was successfully delivered to ${project.client}. Contact us to discuss similar solutions for your business.`,
                                );
                              }}
                            >
                              <Globe className="mr-2 h-4 w-4" />
                              View Project Details
                            </Button>
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => navigate("/contact")}
                            >
                              <ArrowRight className="mr-2 h-4 w-4" />
                              Start Similar Project
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </DialogContent>
            </Dialog>
          )}

          {/* Bottom CTA */}
          <div className="text-center p-8 bg-gradient-subtle rounded-lg">
            <h3 className="text-xl font-semibold mb-2">
              Ready to Start Your Project?
            </h3>
            <p className="text-muted-foreground mb-4">
              Let us help you create the next success story. Our team is ready
              to bring your vision to life.
            </p>
            <div className="flex justify-center gap-4">
              <Button size="lg">
                <ArrowRight className="mr-2 h-4 w-4" />
                Discuss Your Project
              </Button>
              <Button variant="outline" size="lg">
                <Calendar className="mr-2 h-4 w-4" />
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioDialog;
